/**
 * Tool execution, in the visitor's browser.
 *
 * This file is the security story in one place: we receive a tool NAME and
 * ARGUMENTS from our server, and the credential never leaves this page. Verb
 * cannot leak a user's session because Verb never receives one.
 *
 * Two paths:
 *   adapter, the customer's own authenticated client runs it. Works with
 *             Supabase, Firebase, GraphQL, tRPC, a separate API host, and every
 *             stack not yet invented. See docs/09.
 *   browser, same-origin fetch, cookie rides along. Zero config, and genuinely
 *             a five-minute install for the stacks it fits.
 */
import type { ClientToolConfig, ToolOutcomeStatus } from "@verb/shared";
import { isHandle, resolve } from "./files.js";

/** The execution modes a browser is ever asked to run. */
const MODES: readonly string[] = ["browser", "adapter", "mcp", "server", "service", "oauth"];

/**
 * The details of ONE action, as sent with its call, turned into what executeTool
 * needs. Checked rather than trusted: it arrived over the network and decides which
 * URL this page fetches, so anything that is not the shape we sent is refused and
 * the call is reported as unknown rather than guessed at.
 *
 * 🔴 THE PATH ARRIVES WITH THE CALL, NEVER IN A LIST UP FRONT. See
 *    backend/src/agent/exec.ts for why the browser is sent this at all.
 */
export function toolFromExec(name: string, exec: unknown): ClientToolConfig | null {
  if (!exec || typeof exec !== "object") return null;
  const e = exec as Record<string, unknown>;
  if (typeof e["mode"] !== "string" || !MODES.includes(e["mode"])) return null;
  const c = e["classification"];
  if (c !== "read" && c !== "write" && c !== "destructive") return null;
  return {
    name,
    execMode: e["mode"] as ClientToolConfig["execMode"],
    classification: c,
    ...(typeof e["urlTemplate"] === "string" ? { urlTemplate: e["urlTemplate"] } : {}),
    ...(typeof e["method"] === "string" ? { httpMethod: e["method"] } : {}),
  };
}

export type AdapterFn = (toolName: string, args: Record<string, unknown>) => Promise<unknown>;

export interface ExecutionResult {
  readonly status: ToolOutcomeStatus;
  readonly result: unknown;
  /**
   * Whether we have positive evidence the effect happened.
   *
   * Set ONLY from the transport's own answer, a 2xx, or an adapter returning
   * without throwing. Never inferred from anything the model says. This feeds
   * the "97% of actions confirmed" number, which is worth exactly nothing if it
   * can be talked into being true.
   */
  readonly verified: boolean;
}

/**
 * Swaps file handles for the real File objects.
 *
 * 🔴 AN UNKNOWN HANDLE FAILS THE CALL RATHER THAN TRAVELLING AS A STRING. A
 *    value shaped like a handle that this page never minted did not come from a
 *    file picker, so sending it on would put the literal text `file_deadbeef`
 *    into the customer's API where a photograph belongs, and nothing would
 *    report it. docs/40 section 3.1.
 */
function resolveHandles(
  args: Record<string, unknown>,
): { ok: true; args: Record<string, unknown>; files: string[] } | { ok: false; error: string } {
  const out: Record<string, unknown> = {};
  const files: string[] = [];

  for (const [key, value] of Object.entries(args)) {
    if (typeof value === "string" && /^file_[a-z0-9]+$/i.test(value)) {
      if (!isHandle(value)) {
        return {
          ok: false,
          error:
            `The file for "${key}" is no longer available, so nothing was sent. Ask the user to ` +
            `attach it again. Do not claim the upload happened.`,
        };
      }
      const file = resolve(value);
      if (!file) {
        return { ok: false, error: `The file for "${key}" could not be read, so nothing was sent.` };
      }
      out[key] = file;
      files.push(key);
      continue;
    }
    out[key] = value;
  }

  return { ok: true, args: out, files };
}

export async function executeTool(
  tool: ClientToolConfig,
  args: Record<string, unknown>,
  adapter: AdapterFn | null,
): Promise<ExecutionResult> {
  const resolved = resolveHandles(args);
  if (!resolved.ok) {
    return { status: "error", result: { error: resolved.error }, verified: false };
  }
  args = resolved.args;

  if (tool.execMode === "adapter") {
    if (!adapter) {
      // Fail loudly and specifically. A generic "tool failed" sends them hunting
      // through API logs for a request that was never made.
      return {
        status: "error",
        result: {
          error:
            // Model-facing, so it names no code: the model repeats what it is
            // told, and a visitor who asks "why" should hear a plain reason.
            `This action is not connected to the site yet.`,
        },
        verified: false,
      };
    }
    // The adapter receives the real File. Their own code already knows how to
    // upload to their own storage, because it is the code that does it
    // everywhere else in their product.
    return runAdapter(tool, args, adapter);
  }
  return sameOriginFetch(tool, args, resolved.files);
}

async function runAdapter(
  tool: ClientToolConfig,
  args: Record<string, unknown>,
  adapter: AdapterFn,
): Promise<ExecutionResult> {
  try {
    const raw = await adapter(tool.name, args);

    /**
     * 🔴 `undefined` IS A MISSING `case`, NOT AN EMPTY RESULT, and saying so is
     *    the difference between the customer fixing their switch in a minute
     *    and debugging the model for an afternoon. A switch that falls through
     *    returns undefined, and every other reading of that, "no rows", "not
     *    found", "nothing to report", sends them looking in the wrong place.
     *
     *    Distinguished from `null`, which a real handler returns on purpose
     *    when there genuinely is nothing. doc 28 section 3a.
     */
    if (raw === undefined) {
      /**
       * 🔴 undefined CANNOT TELL A VOID MUTATION THAT RAN FROM A SWITCH THAT
       *    FELL THROUGH. Both are undefined. The old code called it a missing
       *    case and told the model "do not claim it was done"; on
       *    cal.askverb.com (2026-10-06) a reschedule that HAD cancelled the
       *    booking was reported as "not wired up, nothing was cancelled". A
       *    write that runs and returns nothing is normal: cancel, delete,
       *    update, reschedule.
       *
       *    So undefined is never called failure here. It is not called success
       *    either, because undefined is no evidence the write took effect, it
       *    may have silently done nothing. The only honest state is UNKNOWN,
       *    and the model is told to say exactly that rather than assert either
       *    outcome. Letting the agent phrase the uncertainty is the point: we
       *    hand it the truth and it tells the user to check.
       *
       *    A genuinely missing case is caught with CERTAINTY elsewhere: the
       *    adapter's documented `default: throw` lands in the catch below as a
       *    real error (see the Unknown-tool branch there), and a site with no
       *    adapter at all is refused before runAdapter is reached. doc 28 3a.
       */
      if (tool.classification === "read") {
        // A read with nothing to hand back is just no data, same as null.
        return {
          status: "empty",
          result: { ok: null, note: `"${tool.name}" ran and returned no data.` },
          verified: false,
        };
      }
      // write / destructive: it ran without error but gave no confirmation.
      return {
        status: "empty",
        result: {
          ok: null,
          note:
            `"${tool.name}" ran without an error but returned nothing, so whether it ` +
            `actually took effect is unknown. Do NOT tell the user it succeeded or that it ` +
            `failed. Tell them you can't confirm it went through and they should check.`,
        },
        verified: false,
      };
    }

    /**
     * 🔴 GRAPHQL RETURNS 200 WITH AN `errors` ARRAY. It is the most-deployed
     *    API style that reports failure inside a successful response, and
     *    without this a failed mutation reaches the model as data.
     *    Checked before the `error` shape below, because a GraphQL envelope
     *    carries `data` alongside `errors` and would otherwise read as a
     *    Supabase result whose `error` key happens to be absent.
     */
    if (raw && typeof raw === "object" && Array.isArray((raw as { errors?: unknown }).errors)) {
      const errors = (raw as { errors: unknown[] }).errors;
      if (errors.length > 0) {
        const first = errors[0];
        const message =
          first && typeof first === "object" && "message" in first
            ? String((first as { message: unknown }).message)
            : describe(first);
        return {
          status: "error",
          result: { error: message, errorCount: errors.length },
          verified: false,
        };
      }
    }

    // Supabase-shaped clients resolve with { data, error } instead of throwing.
    // Treating that as success would report a failed write as done, the exact
    // 2026-08-14 failure, on someone else's database.
    if (raw && typeof raw === "object" && "error" in raw) {
      const err = (raw as { error: unknown }).error;
      if (err) {
        return { status: "error", result: { error: describe(err) }, verified: false };
      }
      const data = (raw as { data?: unknown }).data;
      return { status: data == null ? "empty" : "ok", result: data ?? null, verified: data != null };
    }

    /**
     * 🔴 AN AXIOS RESPONSE RETURNED AS-IS, NOT { data, error }.
     *
     *    `return apiClient.get(url)` is the natural thing to write, and it does
     *    not throw on a 2xx, so this never surfaces as an error. What reaches
     *    the model instead is the whole envelope, `status`, `headers`,
     *    `config`, the redundant `request` object, with the actual payload
     *    buried one level down in `.data`. The tool reports OK and the model
     *    reasons over noise instead of the answer.
     *
     *    The shape is distinctive enough to unwrap safely: a numeric `status`
     *    alongside a `headers` object is not a thing a hand-written API result
     *    would coincidentally have, and no adapter shape we support uses those
     *    names for anything else.
     */
    if (
      raw &&
      typeof raw === "object" &&
      "data" in raw &&
      typeof (raw as { status?: unknown }).status === "number" &&
      typeof (raw as { headers?: unknown }).headers === "object"
    ) {
      const data = (raw as { data?: unknown }).data;
      return { status: data == null ? "empty" : "ok", result: data ?? null, verified: true };
    }

    return { status: raw == null ? "empty" : "ok", result: raw ?? null, verified: raw != null };
  } catch (err) {
    const message = describe(err);
    /**
     * 🔴 THE ONE CERTAIN "NOT WIRED UP" SIGNAL. The documented adapter ends
     *    its switch with `default: throw new Error(...Unknown tool...)`, so a
     *    missing case THROWS rather than silently returning undefined. That
     *    throw, and only that, proves the case is absent, so the helpful "add a
     *    case" message lives here now, not on a bare undefined above.
     */
    if (/unknown tool/i.test(message)) {
      return {
        status: "error",
        result: {
          error:
            // Plain words only. The tool's internal name and the thrown text stay
            // out: both would be repeated to the visitor as implementation detail.
            `This action is not wired up yet, so tell the user it is not available and do not claim it ran.`,
        },
        verified: false,
      };
    }
    return { status: "error", result: { error: message }, verified: false };
  }
}

async function sameOriginFetch(
  tool: ClientToolConfig,
  args: Record<string, unknown>,
  fileKeys: readonly string[] = [],
): Promise<ExecutionResult> {
  if (!tool.urlTemplate) {
    return {
      status: "error",
      result: { error: `Tool "${tool.name}" has no URL configured.` },
      verified: false,
    };
  }

  let url: URL;
  try {
    // The path comes from the SERVER'S tool config, never from model output.
    // Only the placeholder VALUES come from the model, and they are encoded.
    const path = tool.urlTemplate.replace(/\{(\w+)\}/g, (_, key: string) => {
      const value = args[key];
      if (value == null || value === "") throw new Error(`Missing required argument: ${key}`);
      return encodeURIComponent(String(value));
    });

    url = new URL(path, window.location.origin);

    // Same-origin only. A tool whose host is not this page's origin is an
    // adapter or server-mode tool and must not be fetched from here, sending
    // this site's cookie to a third party is precisely the thing we promise
    // never happens.
    if (url.origin !== window.location.origin) {
      throw new Error("Tool host does not match the page origin");
    }
  } catch (err) {
    return { status: "error", result: { error: describe(err) }, verified: false };
  }

  const method = (tool.httpMethod ?? "GET").toUpperCase();
  const bodyless = method === "GET" || method === "HEAD";

  /**
   * 🔴 MULTIPART WHEN THERE IS A FILE, AND NO `content-type` HEADER ON THAT
   *    BRANCH. The boundary is generated by the browser and belongs in the
   *    header it writes; setting `multipart/form-data` by hand omits it and
   *    produces a request the server cannot parse, with an error that reads
   *    like a bug in THEIR code. docs/40 section 3.3.
   *
   * 🔴 THE FORM FIELD NAME IS THE ARGUMENT NAME from verb.md, so the founder
   *    controls it and the skill must read their real endpoint to get it right.
   *    This is the likeliest thing to be wrong on a first install.
   */
  let requestBody: BodyInit | undefined;
  const headers: Record<string, string> = {};

  if (bodyless) {
    requestBody = undefined;
  } else if (fileKeys.length > 0) {
    const form = new FormData();
    for (const [key, value] of Object.entries(args)) {
      if (value instanceof File) form.append(key, value, value.name);
      else if (value !== undefined && value !== null) {
        form.append(key, typeof value === "string" ? value : JSON.stringify(value));
      }
    }
    requestBody = form;
  } else {
    headers["content-type"] = "application/json";
    requestBody = JSON.stringify(args);
  }

  try {
    const res = await fetch(url, {
      method,
      credentials: "same-origin", // the whole point
      headers,
      body: requestBody,
    });

    const body = (await res.text()).slice(0, 8000);

    /**
     * 🔴 A WEB PAGE WHERE DATA WAS EXPECTED MEANS THE API IS NOT HERE, 2026-09-23.
     *    A browser action always goes to the page's own domain. When the app's
     *    API really lives on another one (api.acme.com beside app.acme.com),
     *    the request lands on the app, and a single-page app answers ANY path
     *    with its own index.html, usually with a 200. That read as success and
     *    handed the model a page of markup. Now it is a failure that says what
     *    is wrong, to the model, to the console, and into the dashboard's
     *    failing-tools list, from the first real call in test.
     */
    const contentType = res.headers.get("content-type") ?? "";
    if (/text\/html/i.test(contentType) || /^\s*<(!doctype|html)/i.test(body)) {
      const message =
        `${url.pathname} on ${window.location.host} answered with a web page, not data. ` +
        `This app's API is probably on a different domain; if so, this action needs "run: adapter" in verb.md.`;
      console.warn(`[Verb] ${message}`);
      return {
        status: "error",
        result: { ok: false, status: res.status, error: message, code: "api_not_here" },
        verified: false,
      };
    }

    // Return the failure rather than throwing. The agent needs to SEE a 403 to
    // say "that needs a Pro plan" honestly. Never model the customer's
    // entitlements ourselves: their API decides, and it is right on the day we
    // would be wrong.
    return {
      status: res.ok ? "ok" : "error",
      result: { ok: res.ok, status: res.status, body },
      verified: res.ok,
    };
  } catch (err) {
    // A network-level failure. We do NOT know whether the request reached the
    // server, so this is never `verified` and never `ok`.
    return { status: "error", result: { error: describe(err) }, verified: false };
  }
}

function describe(err: unknown): string {
  if (err instanceof Error) return err.message;
  if (typeof err === "string") return err;
  try {
    return JSON.stringify(err).slice(0, 500);
  } catch {
    return "Unknown error";
  }
}
